import {
    CreateButton,
    DataTable,
    ExportButton,
    List,
    SearchInput,
} from '@/components/admin';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { AlertTriangle, DollarSign, Package } from 'lucide-react';
import { useGetList, useRecordContext } from 'ra-core';
import { TopToolbar } from '../layout/TopToolbar';

const StockIndicator = () => {
    const record = useRecordContext();
    if (!record) return null;
    const stock = record.current_stock ?? 0;
    const min = record.min_stock_level ?? 0;
    const isLow = stock <= min && min > 0;
    return (
        <div className="flex items-center gap-2">
            <span className={`font-bold text-sm ${isLow ? 'text-red-600' : 'text-slate-900'}`}>
                {stock}
            </span>
            {isLow && <AlertTriangle className="w-3.5 h-3.5 text-red-500" />}
            <span className="text-xs text-slate-400">{record.unit || ''}</span>
        </div>
    );
};

const PriceCell = () => {
    const record = useRecordContext();
    if (!record?.unit_price) return <span className="text-slate-400 text-xs">—</span>;
    return (
        <div className="flex items-center gap-1 font-bold text-slate-700">
            <DollarSign className="w-3 h-3 text-slate-400" />
            {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(record.unit_price)}
        </div>
    );
};

const CategoryBadge = () => {
    const record = useRecordContext();
    if (!record?.category) return <span className="text-xs text-slate-400">—</span>;
    return (
        <Badge variant="outline" className="text-[10px] font-semibold bg-slate-50 text-slate-600 border-slate-200">
            <Package className="w-3 h-3 mr-1" />
            {record.category}
        </Badge>
    );
};

const HSNWithGSTCell = () => {
    const record = useRecordContext();
    const hsnCode = record?.hsn_code;

    const { data: hsnData, isPending } = useGetList('hsn_codes', {
        pagination: { page: 1, perPage: 1 },
        filter: { code: hsnCode }
    }, { enabled: !!hsnCode });

    if (!hsnCode) return <span className="text-slate-400 text-xs">—</span>;

    const gstRate = hsnData?.[0]?.gst_rate;

    return (
        <div className="flex items-center gap-1.5">
            <span className="font-mono text-xs text-slate-500">{hsnCode}</span>
            {gstRate !== undefined && !isPending && (
                <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200 px-1 py-0 h-4 min-h-0">
                    {gstRate}%
                </Badge>
            )}
        </div>
    );
};

const InventoryListActions = () => (
    <TopToolbar>
        <ExportButton />
        <CreateButton label="Add Item" />
    </TopToolbar>
);

const filters = [
    <SearchInput key="q" source="q" alwaysOn />,
];

export const InventoryList = () => (
    <List
        title="Inventory Items"
        actions={<InventoryListActions />}
        filters={filters}
        sort={{ field: 'name', order: 'ASC' }}
        className="p-6"
    >
        <Card className="border-none shadow-sm overflow-hidden">
            <DataTable rowClick="show">
                <DataTable.Col source="sku" label="SKU" className="font-mono font-bold text-blue-600" />
                <DataTable.Col source="name" label="Item Name" className="font-medium" />
                <DataTable.Col label="Category">
                    <CategoryBadge />
                </DataTable.Col>
                <DataTable.Col label="Stock Level">
                    <StockIndicator />
                </DataTable.Col>
                <DataTable.NumberCol source="min_stock_level" label="Min Level" />
                <DataTable.Col label="Unit Price">
                    <PriceCell />
                </DataTable.Col>
                <DataTable.Col label="HSN">
                    <HSNWithGSTCell />
                </DataTable.Col>
            </DataTable>
        </Card>
    </List>
);
